import { useCallback, useEffect, useRef, useState } from "react";

export interface Loaded<T> {
  /** The last answer, kept while a newer one loads, so a table never blanks between filters. */
  data: T | null;
  /** Why the latest load failed; cleared by the next success. */
  error: unknown;
  isLoading: boolean;
  /** Load again with the same key (after a write, or on the page's Refresh). */
  reload: () => void;
}

/**
 * One read for a component: loads when `key` changes (or on `reload`), and
 * ignores answers that arrive after a newer request, so a fast typist's last
 * search wins. `key` stands for everything the read depends on.
 */
export function useLoad<T>(load: () => Promise<T>, key: string): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(load);
  // Declared before the load effect, so a load always runs the latest closure.
  useEffect(() => {
    latest.current = load;
  });

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    latest
      .current()
      .then((answer) => {
        if (!isCurrent) return;
        setData(answer);
        setError(null);
      })
      .catch((err: unknown) => {
        if (isCurrent) setError(err);
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, isLoading, reload };
}
